import {
  useEffect,
  useRef,
  useState,
} from "react";
import {
  useParams,
  useNavigate,
  Link,
} from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import "./ProductInfo.css";

function assetUrl(src) {
  if (!src) return "";

  if (
    /^https?:\/\//i.test(src) ||
    src.startsWith("/")
  ) {
    return src;
  }

  return `/${src}`;
}

export default function ProductInfo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [product, setProduct] =
    useState(null);
  const [packId, setPackId] =
    useState(null);
  const [inputs, setInputs] =
    useState({});
  const [role, setRole] =
    useState(null);
  const [busy, setBusy] =
    useState(false);
  const [error, setError] =
    useState(null);
  const [done, setDone] =
    useState(null);
  const [checkoutOpen, setCheckoutOpen] =
    useState(false);

  /*
   * Immediate synchronous protection against
   * rapid double clicks on Pay with UPI.
   *
   * React state updates are asynchronous, so
   * busy alone may not block a second click
   * made before the component re-renders.
   */
  const upiSubmittingRef = useRef(false);

  useEffect(() => {
    api
      .get(`/product/${id}`)
      .then((response) => setProduct(response.data.product))
      .catch((requestError) =>
        setError(requestError.message)
      );
  }, [id]);

  useEffect(() => {
    if (!checkoutOpen) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const closeOnEscape = (event) => {
      if (
        event.key === "Escape" &&
        !busy
      ) {
        setCheckoutOpen(false);
      }
    };

    window.addEventListener(
      "keydown",
      closeOnEscape
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        closeOnEscape
      );
    };
  }, [checkoutOpen, busy]);

  const pack =
    product?.packs.find(
      (item) => item.packId === packId
    ) || null;

  const missing = product
    ? product.fields.some(
        (field) =>
          field.required &&
          !String(
            inputs[field.key] || ""
          ).trim()
      )
    : true;

  const walletEnough =
    !!pack &&
    Number(user?.balance || 0) >=
      Number(pack.price);

  const outOfStock =
    product && product.stock === "no";

  const setField = (key, value) => {
    setInputs((current) => ({
      ...current,
      [key]: value,
    }));

    setRole(null);
  };

  async function verify() {
    if (!packId) {
      setError(
        "Please select a pack first to verify your player."
      );
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const { data } = await api.post(
        "/order/check-role",
        {
          packId,
          gameUserId: inputs.userId,
          gameZoneId: inputs.zoneId,
        }
      );

      setRole({
        username: data.username,
        zone: data.zone,
      });
    } catch (requestError) {
      setError(requestError.message);
      setRole(null);
    } finally {
      setBusy(false);
    }
  }

  function openCheckout() {
    if (!user) {
      navigate("/login", {
        state: {
          from: {
            pathname: `/game/${id}`,
          },
        },
      });

      return;
    }

    if (!pack) {
      setError("Please choose a pack.");
      return;
    }

    if (missing) {
      setError(
        "Please complete all required account details."
      );
      return;
    }

    setError(null);
    setCheckoutOpen(true);
  }

  async function payWithWallet() {
    if (!walletEnough) {
      setError(
        "Not enough wallet balance. Choose UPI or add money to your wallet."
      );
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const { data } = await api.post(
        "/order/create",
        {
          packId,
          inputs,
        }
      );

      setCheckoutOpen(false);
      setDone(data);

      await refreshUser();
    } catch (requestError) {
      setError(
        requestError.code ===
          "INSUFFICIENT_BALANCE"
          ? "Not enough wallet balance. Choose UPI or add money to your wallet."
          : requestError.message
      );
    } finally {
      setBusy(false);
    }
  }

  async function payWithUpi() {
    /*
     * Block another request immediately, even
     * before React updates the busy state.
     */
    if (upiSubmittingRef.current) {
      return;
    }

    upiSubmittingRef.current = true;
    setBusy(true);
    setError(null);

    try {
      const { data } = await api.post(
        "/order/create-upi",
        {
          packId,
          inputs,
        }
      );

      if (!data.paymentUrl) {
        throw new Error(
          "UPI payment link was not returned."
        );
      }

      window.location.assign(
        data.paymentUrl
      );
    } catch (requestError) {
      /*
       * Allow retry only when creating the
       * payment link failed.
       */
      upiSubmittingRef.current = false;

      setError(
        requestError.message ||
          "Unable to prepare UPI payment."
      );

      setBusy(false);
    }
  }

  if (error && !product) {
    return (
      <div className="pi-empty">
        {error}
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pi-empty muted">
        Loading…
      </div>
    );
  }

  if (done) {
    return (
      <div className="pi-done">
        <h1>Order placed</h1>

        <p className="muted">
          Order {done.orderId} — ₹
          {done.price}
        </p>

        <p className="muted small">
          {done.status === "success"
            ? "Delivered."
            : "Processing. You can follow it in your order history."}
        </p>

        <Link
          className="btn primary"
          to={`/orders/${done.orderId}`}
        >
          View order
        </Link>
      </div>
    );
  }

  return (
    <div className="container pi">
      <header className="pi-head">
        <img
          src={assetUrl(product.image)}
          alt=""
        />
        <div>
          <h1>{product.name}</h1>
          <p className="muted">
            {product.company}
          </p>
        </div>
      </header>
      <div className="pi-trust">
        <span><i className="pi-dot" />24/7 Support</span>
        <span><i className="pi-dot" />Safe & Reliable</span>
        <span><i className="pi-dot" />Instant delivery</span>
      </div>
      <section className="pi-section">
        <h2 className="pi-label">
          Account details
        </h2>

        <div className="pi-fields">
          {product.fields.map((field) => (
            <label
              key={field.key}
              className="pi-field"
            >
              <span className="muted small">
                {field.label}
              </span>

              {field.type === "select" ? (
                <select
                  value={
                    inputs[field.key] || ""
                  }
                  onChange={(event) =>
                    setField(
                      field.key,
                      event.target.value
                    )
                  }
                >
                  <option value="">
                    Select…
                  </option>

                  {(field.options || []).map(
                    (option) => (
                      <option
                        key={option}
                        value={option}
                      >
                        {option}
                      </option>
                    )
                  )}
                </select>
              ) : (
                <input
                  inputMode={
                    field.type === "number"
                      ? "numeric"
                      : "text"
                  }
                  maxLength={
                    field.maxLength
                  }
                  value={
                    inputs[field.key] || ""
                  }
                  onChange={(event) =>
                    setField(
                      field.key,
                      event.target.value
                    )
                  }
                />
              )}
            </label>
          ))}
        </div>

        {product.playerCheck && (
          <div className="pi-verify">
            <button
              className="btn ghost"
              onClick={verify}
              disabled={
                busy ||
                !inputs.userId
              }
            >
              Verify player
            </button>

            {!packId && (
              <span
                className="muted small"
                style={{
                  marginLeft: 8,
                }}
              >
                Select a pack below first
              </span>
            )}

            {role && (
              <span className="pi-ign">
                {role.username}
              </span>
            )}
          </div>
        )}
      </section>

      <section className="pi-section">
        <h2 className="pi-label">
          Choose a pack
        </h2>

        <div className="pi-packs">
          {product.packs.map((item) => (
            <button
              key={item.packId}
              className={`pi-pack${
                packId === item.packId
                  ? " on"
                  : ""
              }`}
              aria-pressed={
                packId === item.packId
              }
              onClick={() =>
                setPackId(item.packId)
              }
            >
              <div className="pi-pack-top">
                {item.image && (
                  <img
                    src={assetUrl(
                      item.image
                    )}
                    alt=""
                  />
                )}

                <strong>
                  {item.amount}
                </strong>
              </div>

              <div className="pi-price tabular">
                ₹{item.price}
              </div>

              {item.strikePrice && (
                <div className="pi-strike muted tabular">
                  ₹{item.strikePrice}
                </div>
              )}
            </button>
          ))}
        </div>
      </section>

      {product.desc && (
        <section className="pi-section">
          <h2 className="pi-label">
            Before you order
          </h2>

          <p className="pi-desc muted">
            {product.desc}
          </p>
        </section>
      )}

      {error && (
        <p
          role="alert"
          className="alert bad"
        >
          {error}
        </p>
      )}

      <div className="pi-buy">
        {user && (
          <span className="muted small">
            Wallet:{" "}
            <strong className="tabular">
              ₹
              {Number(
                user.balance
              ).toFixed(2)}
            </strong>
          </span>
        )}

        {outOfStock && (
          <div
            style={{
              textAlign: "center",
              padding: "12px 0",
            }}
          >
            <span
              style={{
                display: "inline-block",
                padding: "10px 24px",
                background:
                  "rgba(244,63,94,.1)",
                color: "#f43f5e",
                borderRadius: 10,
                fontWeight: 700,
                fontSize: 14,
                border:
                  "1px solid rgba(244,63,94,.2)",
              }}
            >
              Out of Stock
            </span>
          </div>
        )}

        <button
          className="btn primary big"
          onClick={openCheckout}
          disabled={
            busy ||
            !pack ||
            missing ||
            outOfStock
          }
        >
          {outOfStock
            ? "Out of Stock"
            : !pack
              ? "Choose a pack"
              : `Buy Now • ₹${pack.price}`}
        </button>
      </div>

      {checkoutOpen && pack && (
        <div
          className="pi-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target ===
                event.currentTarget &&
              !busy
            ) {
              setCheckoutOpen(false);
            }
          }}
        >
          <div
            className="pi-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-title"
          >
            <button
              type="button"
              className="pi-modal-close"
              aria-label="Close checkout"
              onClick={() =>
                setCheckoutOpen(false)
              }
              disabled={busy}
            >
              ×
            </button>

            <div className="pi-checkout-game">
              <img
                src={assetUrl(
                  product.image
                )}
                alt=""
              />

              <div>
                <div className="muted small">
                  Game
                </div>

                <h2 id="checkout-title">
                  {product.name}
                </h2>

                {product.company && (
                  <p className="muted small">
                    {product.company}
                  </p>
                )}
              </div>
            </div>

            <div className="pi-summary">
              <h3>Account details</h3>

              {product.fields.map(
                (field) => (
                  <div
                    className="pi-summary-row"
                    key={field.key}
                  >
                    <span>
                      {field.label}
                    </span>

                    <strong>
                      {inputs[
                        field.key
                      ] || "—"}
                    </strong>
                  </div>
                )
              )}

              {role?.username && (
                <div className="pi-summary-row">
                  <span>
                    Player name
                  </span>

                  <strong className="pi-checkout-player">
                    {role.username}
                  </strong>
                </div>
              )}
            </div>

            <div className="pi-summary">
              <h3>Pack details</h3>

              <div className="pi-summary-row">
                <span>Pack</span>
                <strong>
                  {pack.amount}
                </strong>
              </div>

              <div className="pi-summary-row pi-summary-total">
                <span>Total</span>
                <strong>
                  ₹{pack.price}
                </strong>
              </div>
            </div>

            <h3 className="pi-payment-title">
              Choose payment method
            </h3>

            <div className="pi-payment-options">
              <button
                type="button"
                className="pi-payment-option"
                onClick={payWithWallet}
                disabled={
                  busy ||
                  !walletEnough
                }
              >
                <span className="pi-payment-icon">
                  ₹
                </span>

                <span className="pi-payment-copy">
                  <strong>
                    Wallet
                  </strong>

                  <small>
                    Balance ₹
                    {Number(
                      user.balance
                    ).toFixed(2)}
                    {!walletEnough
                      ? " • Insufficient balance"
                      : ""}
                  </small>
                </span>

                <span className="pi-payment-arrow">
                  ›
                </span>
              </button>

              <button
                type="button"
                className="pi-payment-option pi-payment-upi"
                onClick={payWithUpi}
                disabled={busy}
              >
                <span className="pi-payment-icon">
                  UPI
                </span>

                <span className="pi-payment-copy">
                  <strong>
                    Pay with UPI
                  </strong>

                  <small>
                    Google Pay, PhonePe,
                    Paytm or any UPI app
                  </small>
                </span>

                <span className="pi-payment-arrow">
                  ›
                </span>
              </button>
            </div>

            {busy && (
              <p className="pi-checkout-loading">
                Preparing secure payment…
              </p>
            )}

            {error && (
              <p className="alert bad pi-modal-error">
                {error}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
